import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { api, type AuthUser } from '../../lib/api'
import { isAdminRole, signOut } from '../../lib/auth'
import { usePreferences } from '../../lib/preferences'
import { Topbar } from './Topbar'
import { Sidebar } from './Sidebar'
import { AppFooter } from './AppFooter'
import { CommandPalette } from './CommandPalette'

/** Authenticated app chrome: topbar + collapsible sidebar + routed content. */
export function AppShell({ user, children }: { user: AuthUser; children: ReactNode }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { sidebar, setCollapsed, theme, toggleTheme } = usePreferences()
  const [searchOpen, setSearchOpen] = useState(false)
  const canSearch = isAdminRole(user)

  useEffect(() => {
    if (!canSearch) return
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canSearch])

  async function handleLogout() {
    try {
      await api.logout()
    } catch {
      /* ignore */
    }
    await signOut(queryClient)
    navigate({ to: '/login' })
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg)] text-[var(--fg)]">
      <Topbar
        user={user}
        theme={theme}
        onToggleTheme={toggleTheme}
        onToggleSidebar={() => setCollapsed(!sidebar.collapsed)}
        onOpenSearch={() => setSearchOpen(true)}
        canSearch={canSearch}
        onLogout={handleLogout}
      />
      <div className="flex flex-1 min-h-0">
        <Sidebar />
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden flex flex-col">
          <div className="flex-1">{children}</div>
          <AppFooter />
        </main>
      </div>
      {canSearch && <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />}
    </div>
  )
}
